// @ts-check
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import { Nav } from './components/nav.js';
import { GalleryRunway } from './components/gallery-runway.js';
import { ChapterRail } from './components/chapter-rail.js';
import { Lightbox } from './components/lightbox.js';
import { AboutSection } from './components/about-section.js';
import { ContactSection } from './components/contact-section.js';
import { Footer } from './components/footer.js';
import { CustomCursor } from './components/cursor.js';
import { HeroMarquee } from './components/hero-marquee.js';
import { drawCracks } from './crack-canvas.js';
import {
  HERO_T_END,
  RUNWAY_T_SPAN,
  RUNWAY_T_END,
  RUNWAY_SEG,
  HALL_BOUNDARIES,
  r2t,
} from './timeline.js';
import { computeScale, computeType, computeModelGain } from './scale-model.js';
import './styles/main.css';

gsap.registerPlugin(ScrollTrigger);

/**
 * Fetches static JSON data from the public/data directory.
 * @param {string} path
 * @returns {Promise<Object>}
 */
async function loadData(path) {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Failed to load ${path}: ${response.status}`);
  }
  return response.json();
}



/** @param {number} r */
const C = (r) => 2 * Math.PI * r;

/**
 * Creates a GSAP-driven geometric loading animation (self-drawing mandala).
 * No visible progress — the animation IS the experience.
 * @param {HTMLElement} overlay
 * @param {number} totalResources
 * @returns {{ advance: () => void, ready: Promise<void> }}
 */
function createLoadingTracker(overlay, totalResources) {
  let loaded = 0;
  let entranceDone = false;
  let loadingDone = false;
  /** @type {() => void} */
  let resolveReady;
  const ready = new Promise((r) => { resolveReady = r; });

  const geo = overlay.querySelector('.loading-geo');
  const c1 = document.getElementById('loading-c1');
  const c2 = document.getElementById('loading-c2');
  const c3 = document.getElementById('loading-c3');
  const c4 = document.getElementById('loading-c4');
  const rays = overlay.querySelectorAll('.loading-ray');
  const dots = overlay.querySelectorAll('.loading-dot');
  if (!geo || !c1 || !c2 || !c3 || !c4) return { advance() {}, ready: Promise.resolve() };

  // Set initial stroke-dash states.
  gsap.set(c1, { attr: { 'stroke-dasharray': C(24), 'stroke-dashoffset': C(24) } });
  gsap.set(c2, { attr: { 'stroke-dasharray': C(50), 'stroke-dashoffset': C(50) } });
  gsap.set(c3, { attr: { 'stroke-dasharray': C(80), 'stroke-dashoffset': C(80) } });
  gsap.set(c4, { attr: { 'stroke-dasharray': C(110), 'stroke-dashoffset': C(110) } });
  gsap.set(rays, { attr: { 'stroke-dasharray': '200', 'stroke-dashoffset': '200' } });
  gsap.set(dots, { opacity: 0, scale: 0, transformOrigin: '50% 50%' });
  gsap.set(geo, { opacity: 0, scale: 0.85, transformOrigin: '50% 50%' });

  // Entrance timeline — the main visual event.
  const entrance = gsap.timeline({
    onComplete() { entranceDone = true; tryExit(); },
  });

  entrance
    .to(geo, { opacity: 1, scale: 1, duration: 0.8, ease: 'power2.out' })
    // Circles draw from core → outer, staggered.
    .to(c1, { attr: { 'stroke-dashoffset': 0 }, duration: 1.4, ease: 'power2.inOut' }, 0.2)
    .to(c2, { attr: { 'stroke-dashoffset': 0 }, duration: 1.6, ease: 'power2.inOut' }, 0.5)
    .to(c3, { attr: { 'stroke-dashoffset': 0 }, duration: 1.8, ease: 'power2.inOut' }, 0.8)
    .to(c4, { attr: { 'stroke-dashoffset': 0 }, duration: 2.0, ease: 'power2.inOut' }, 1.1)
    // Radial lines extend from center with stagger.
    .to(rays, { attr: { 'stroke-dashoffset': 0 }, duration: 1.1, ease: 'power3.out', stagger: 0.06 }, 0.6)
    // Dots pop in.
    .to(dots, { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(3)', stagger: 0.06 }, 1.8);

  // Continuous slow rotation — independent of entrance.
  const rotTween = gsap.to(geo, { rotation: 360, duration: 30, repeat: -1, ease: 'none' });

  // Subtle pulse on core ring.
  gsap.to(c1, { opacity: 0.5, duration: 2.5, repeat: -1, yoyo: true, ease: 'sine.inOut' });

  function tryExit() {
    if (!entranceDone || !loadingDone) return;

    // Ensure minimum display time (entrance duration ~3.5s).
    gsap.killTweensOf(geo);
    rotTween.kill();

    const exit = gsap.timeline({
      onComplete() {
        overlay.style.display = 'none';
        resolveReady();
      },
    });

    // Reverse: dots → rays → circles outer→inner → overlay.
    exit
      .to(dots, { opacity: 0, scale: 0, duration: 0.3, stagger: 0.03, ease: 'power2.in' })
      .to(rays, { attr: { 'stroke-dashoffset': 200 }, duration: 0.5, ease: 'power2.in', stagger: 0.02 }, 0.1)
      .to(c4, { attr: { 'stroke-dashoffset': C(110) }, duration: 0.5, ease: 'power2.in' }, 0.15)
      .to(c3, { attr: { 'stroke-dashoffset': C(80) }, duration: 0.45, ease: 'power2.in' }, 0.25)
      .to(c2, { attr: { 'stroke-dashoffset': C(50) }, duration: 0.4, ease: 'power2.in' }, 0.35)
      .to(c1, { attr: { 'stroke-dashoffset': C(24) }, duration: 0.35, ease: 'power2.in' }, 0.45)
      .to(overlay, { opacity: 0, duration: 0.8, ease: 'power3.inOut' }, 0.6);
  }

  function advance() {
    loaded += 1;
    if (loaded >= totalResources) {
      loadingDone = true;
      tryExit();
    }
  }

  return { advance, ready };
}

// ── Scale model ─────────────────────────────────────────────────────────────
// One reference frame (1440×900 in src/scale-model.js), two ratios published
// as CSS variables, no per-breakpoint magic numbers. See that module for the
// rationale; the CSS contract is:
//
//   1. GEOMETRY  `min(<vw cap>, <desktop px> * var(--scale))`
//      Every authored px becomes "desktop px × scale", so the element keeps
//      the fraction of the screen it has on desktop; the `min()` cap is a
//      second line of defence against the viewport edge. Anything already
//      authored in vw/vh keeps its vw/vh — proportional by construction.
//
//   2. TYPE      `min(<desktop vw expr>, <cap px> * var(--type))` for display
//      type, plus `html { font-size: 16px * var(--type) }` for everything in
//      rem. The root declaration is what makes the small copy (kickers,
//      captions, meta lines) move with the viewport — left in plain rem they
//      stay pixel-identical from 1440 down to 360 and look "unscaled".
//
// The same `scale` drives the WebGL camera, so the sculpture shrinks with the
// layout instead of being pinned to the viewport height.

function applyScaleModel() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const root = document.documentElement.style;
  root.setProperty('--scale', String(computeScale(vw, vh)));
  root.setProperty('--type', String(computeType(vw)));
}

/**
 * Publishes the browser-chrome / OS safe-area insets as CSS custom properties.
 *
 * `window.innerHeight` is the *layout* viewport — the full rectangle the page
 * may occupy, which on a real phone includes the band the browser's address
 * bar and bottom toolbar sit over, plus the OS status bar / home indicator.
 * What the user actually sees is `visualViewport`, which is smaller and offset.
 * A hall header pinned `bottom: 8px` from the layout viewport, or the BGM
 * toggle at `bottom: 24px`, therefore lands *under* that chrome and is partly
 * hidden. `visualViewport` reports the true visible rectangle, so we derive the
 * top/bottom chrome insets from it and let CSS inset every fixed and
 * bottom-anchored element by them (combined with env(safe-area-inset-*) for the
 * notch / home indicator). The sculpture reserves stay in layout-viewport
 * coordinates and the chrome band is added on the CSS side, so the two never
 * drift apart.
 */
function applyVisualViewport() {
  const root = document.documentElement.style;
  const vv = window.visualViewport;
  if (vv) {
    const layoutH = window.innerHeight;
    const topInset = Math.max(0, vv.offsetTop);
    const bottomInset = Math.max(0, layoutH - vv.height - vv.offsetTop);
    root.setProperty('--vv-top', `${Math.round(topInset)}px`);
    root.setProperty('--vv-bottom', `${Math.round(bottomInset)}px`);
    root.setProperty('--vv-h', `${Math.round(vv.height)}px`);
  } else {
    root.setProperty('--vv-top', '0px');
    root.setProperty('--vv-bottom', '0px');
    root.setProperty('--vv-h', `${window.innerHeight}px`);
  }
}

/**
 * Main application entry point.
 */
async function init() {
  const navContainer = document.getElementById('nav');
  const heroContainer = document.getElementById('hero-canvas');
  const runwaySection = document.getElementById('runway');
  const aboutContent = document.getElementById('about-content');
  const contactContent = document.getElementById('contact-content');
  const lightboxContainer = document.getElementById('lightbox');
  const footerContainer = document.getElementById('footer');
  const railContainer = document.getElementById('chapter-rail');

  if (!navContainer || !heroContainer || !runwaySection || !aboutContent || !contactContent || !lightboxContainer || !footerContainer || !railContainer) {
    console.error('Missing required DOM containers in index.html');
    return;
  }

  applyScaleModel();
  window.addEventListener('resize', applyScaleModel, { passive: true });
  window.addEventListener('orientationchange', applyScaleModel, { passive: true });

  // Real-device chrome / safe-area insets. Run once now and on any viewport
  // change; mobile browsers move the chrome without always firing a
  // layout-affecting resize, so the visualViewport events are the reliable
  // signal. The reserve re-measure that depends on these lives further down,
  // wired to the same events inside the scroll driver.
  applyVisualViewport();
  window.addEventListener('resize', applyVisualViewport, { passive: true });
  window.addEventListener('orientationchange', applyVisualViewport, { passive: true });

  // Static fallback is for REDUCED MOTION only.
  //
  // It used to also trigger on `(max-width: 767px)`, which stripped phones of
  // the sculpture, the glass cracks and the corridor and served a plain
  // vertically-stacked document instead — a completely different, generic
  // experience rather than the same one scaled down. Phones now run the full
  // pipeline; the layout is scaled in CSS, not replaced.
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isStatic = reducedMotion;
  document.body.classList.toggle('is-static-mode', isStatic);

  // The animated loader is tied to the WebGL entrance. Static mode reveals
  // immediately so reduced-motion visitors never wait for an animation.
  const overlay = document.getElementById('loading-overlay');
  const tracker = overlay && !isStatic ? createLoadingTracker(overlay, 1) : null;
  if (overlay && isStatic) {
    overlay.hidden = true;
    overlay.style.display = 'none';
  }

  new Nav(navContainer, {
    links: [
      { id: 'hero', label: 'Home' },
      { id: 'runway', label: 'Works' },
      { id: 'about', label: 'About' },
      { id: 'contact', label: 'Contact' },
    ],
  });

  // Hero marquee: clone the card set enough times for a gap-free infinite
  // loop on any viewport width.
  const marqueeEl = document.querySelector('.hero-marquee');
  const heroMarquee =
    marqueeEl instanceof HTMLElement ? new HeroMarquee(marqueeEl) : null;

  const heroContent = document.querySelector('.hero-content');
  /** @type {{ destroy: () => void }|null} */
  let crackCleanup = null;

  // Flags for entrance sequencing.
  let davidReady = false;
  let cracksDone = false;
  let entranceStarted = false;

  /** Play David's hero entrance. Gated on `cracksDone` — the crack canvas
   *  only starts drawing after the loading overlay has fully exited, so the
   *  entrance is never played behind the overlay (which hid it entirely).
   *  Runs at most once. */
  function tryDavidEntrance() {
    // Reveal is decoupled from the stage: with WebGL unavailable there is no
    // sculpture to fly in, but the headline must still appear.
    if (entranceStarted || !cracksDone) return;
    if (stage && !davidReady) return;
    entranceStarted = true;

    if (stage) {
      const entrance = { progress: 0 };
      stage.startEntrance();
      gsap.to(entrance, {
        progress: 1,
        duration: 0.8,
        ease: 'power3.out',
        onUpdate: () => stage?.setEntranceProgress(entrance.progress),
      });
    }
    // Text reveals together with David (or alone, if the stage never came up).
    gsap.delayedCall(0.3, () => heroContent?.classList.add('is-revealed'));
  }

  // The WebGL stage (and the whole three.js bundle) is loaded on demand via
  // dynamic import, so the entry chunk stays small. Static mode never imports
  // it: phones get the editorial layout without model downloads or GPU work.
  /** @type {import('./scenes/spirit-stage.js').SpiritStage | null} */
  let stage = null;

  /**
   * Boots the WebGL stage. NEVER throws and NEVER leaves the loader stuck.
   *
   * `new SpiritStage()` throws when a WebGL context cannot be created — no GPU
   * acceleration, a blocklisted driver, a VM, remote desktop, or headless
   * rendering. That used to reject this promise, abort `init()` at the
   * `await`, and leave the loading overlay (z-index 300, pointer-events auto)
   * parked over the page forever: an entirely dead site with no working nav.
   * A hero without 3D is a degraded experience; a hero that cannot be
   * dismissed is a broken one.
   */
  async function loadStage() {
    try {
      const { SpiritStage } = await import('./scenes/spirit-stage.js');
      stage = new SpiritStage(heroContainer, {
        models: {
          david: `${import.meta.env.BASE_URL}models/David-lite.glb`,
          thinker: `${import.meta.env.BASE_URL}models/Rodin-lite.glb`,
          venus: `${import.meta.env.BASE_URL}models/Venus-lite.glb`,
        },
        renderEnabled: true,
        onPriorityReady: (ok) => {
          davidReady = ok;
          // Release the loader as soon as David is loaded (or has failed) so a
          // slow model never traps the user on the overlay. The hero entrance
          // itself is gated on the crack animation further down, so it plays
          // AFTER the overlay lifts and therefore stays visible.
          tracker?.advance();
        },
        // Extra safety: a real composited frame also counts as "loaded".
        onFirstRender: () => {
          tracker?.advance();
        },
        onModelReady: (key, dataUrl) => {
          document.querySelectorAll(`[data-still="${key}"]`).forEach((img) => {
            /** @type {HTMLImageElement} */ (img).src = dataUrl;
          });
        },
      });
      // Debug handle: `__stage.qualityLevel` reports the adaptive tier
      // (0 = full, 1 = reduced). Handy when diagnosing a laggy machine.
      window.__stage = stage;
    } catch (error) {
      // Failure path only: hand back the loader. On the success path the
      // onPriorityReady / onFirstRender callbacks own it, so the overlay still
      // waits for real assets.
      console.error('[stage] WebGL unavailable — serving the static hero.', error);
      stage = null;
      davidReady = true;
      tracker?.advance();
    }
  }

  /** @type {Promise<void> | null} */
  let stagePromise = null;
  if (!isStatic) {
    stagePromise = loadStage();
  }

  // Static fallback reveals its text synchronously.
  if (isStatic) {
    heroContent?.classList.add('is-revealed');
  }

  let halls = [];
  let runway = null;
  /** Set by setupBgm once the halls are known; feeds it the runway progress
   *  that drives the per-hall music crossfade. */
  let bgmSetProgress = null;
  /** Tears the per-hall music engine down on page unload. */
  let bgmStop = null;

  try {
    const [exhibitions, aboutData] = await Promise.all([
      loadData(`${import.meta.env.BASE_URL}data/exhibitions.json`),
      loadData(`${import.meta.env.BASE_URL}data/about.json`),
    ]);

    halls = exhibitions.halls ?? [];

    runway = new GalleryRunway(runwaySection, halls, {
      isStatic,
    });

    new AboutSection(aboutContent, aboutData);
    new ContactSection(contactContent, aboutData);
  } catch (error) {
    console.error('Failed to load site data:', error);
  }

  // Desktop: wait for the on-demand stage chunk before wiring scroll visuals
  // (the import started above, so this usually resolves instantly).
  // ── Last-resort loader release ─────────────────────────────────────
  // Registered BEFORE awaiting the stage: if boot throws, everything after
  // the await used to be skipped, which is precisely how the overlay ended up
  // parked over a dead page. Two tiers:
  //   15s — hand the loader its final tick and reveal the headline.
  //   20s — hide the overlay outright, whatever state the timeline is in.
  setTimeout(() => {
    tracker?.advance();
    heroContent?.classList.add('is-revealed');
  }, 15000);
  setTimeout(() => {
    const ov = document.getElementById('loading-overlay');
    if (ov && ov.style.display !== 'none') {
      ov.style.opacity = '0';
      ov.style.display = 'none';
    }
  }, 20000);

  if (!isStatic && stagePromise) {
    // loadStage is written not to reject; the catch is defence in depth so a
    // surprise in boot can no longer abort the rest of init.
    await stagePromise.catch((error) => {
      console.error('[stage] boot rejected — continuing without 3D.', error);
    });
  }

  // ── Glass-crack entrance animation ───────────────────────────────
  // Deferred until the loading overlay has fully exited, so the user
  // never misses the hero entrance behind the overlay.
  const cracksCanvas = /** @type {HTMLCanvasElement|null} */ (
    document.getElementById('hero-cracks')
  );
  if (cracksCanvas && !isStatic) {
    (async () => {
      if (tracker) await tracker.ready;
      // Burst from the sculpture itself: its projected hero box is the only
      // anchor that survives the subject's position weight (a portrait
      // viewport clamps the model inward, away from its authored column).
      const heroBox = stage?.heroSubjectScreenRect?.();
      crackCleanup = drawCracks(cracksCanvas, {
        impactScreen: heroBox
          ? { x: heroBox.x + heroBox.w * 0.325, y: heroBox.y + heroBox.h * 0.485 }
          : undefined,
        // Frame-relative fallback for the no-WebGL path, where there is no
        // sculpture to measure.
        impact: { x: 0.30, y: 0.35 },
        // The shards are authored in the same reference frame the camera
        // frames, so the glass stays locked to the composition.
        frame: stage?.framePx,
        // Ray lengths are measured to the SCREEN edge and ray density is
        // weighted by the empty space beyond the composition, so a portrait
        // hero fills its empty lower half instead of stopping at the frame.
        scale: computeScale(window.innerWidth, window.innerHeight)
          * computeModelGain(window.innerWidth, window.innerHeight),
        duration: 1200,
        reducedMotion,
        onComplete: () => {
          cracksDone = true;
          tryDavidEntrance();
        },
      });
    })();
  }

  const rail = new ChapterRail(
    railContainer,
    halls.map((h) => ({ id: h.id, numeral: h.numeral, title: h.title })),
    { onJump: (hallId) => jumpToHall(hallId) }
  );

  /**
   * @param {number} t - story progress
   * @param {number|null} r - runway progress (null while in hero)
   */
  function updateRail(t, r) {
    // Hide rail when past the runway or before entering it.
    rail.setVisible(t > 0.05 && t < RUNWAY_T_END);
    if (r === null || halls.length === 0) {
      rail.setActive(null);
      bgmSetProgress?.(null);
      return;
    }
    // Boundaries are the midpoints between hall centers (see timeline.js),
    // so the rail highlight matches the hall actually on screen.
    const idx = HALL_BOUNDARIES.findIndex((boundary) => r < boundary);
    const activeIdx = idx === -1 ? halls.length - 1 : idx;
    rail.setActive(halls[activeIdx]?.id ?? null);
    // The music hands over across the same boundaries the rail switches on.
    bgmSetProgress?.(r);
  }

  /** @param {string} hallId */
  function jumpToHall(hallId) {
    if (!runway || !lenis) return;
    const y = runway.getJumpScrollY(hallId);
    const distance = Math.abs(y - lenis.scroll);
    lenis.scrollTo(y, { duration: clampJumpDuration(distance), easing: JUMP_EASE });
  }

  /**
   * Duration for a programmatic jump, derived from the distance so a long pan
   * keeps a pace comparable to a short one.
   *
   * A fixed duration cannot: the old 1.9s across one screen was a pleasant
   * pan, but the same 1.9s across three screens tripled the velocity.
   *
   * power2-out peaks at twice its average speed, hence the factor of 2.
   * @param {number} distance
   */
  function clampJumpDuration(distance) {
    return Math.min(3.2, Math.max(1.0, (2 * distance) / MAX_SCROLL_SPEED));
  }

  // ── Lenis smooth scroll driver ─────────────────────────────────────────────
  // Lenis owns window.scrollY. All visual layers read live scrollY /
  // ScrollTrigger progress directly — no second smoothing stage, because
  // double-smoothing reads as lag, not glide.
  const textOverlay = document.getElementById('hero-text-overlay');
  const cracksCanvasEl = /** @type {HTMLElement|null} */ (
    document.getElementById('hero-cracks')
  );
  /** Tracks whether hero text exit is active (bidirectional scroll). */
  let heroExitActive = false;

  /** @type {Lenis | null} */
  let lenis = null;
  /** @type {(time: number) => void} | null */
  let tickerCallback = null;

  /** Cleared on resize so the hall reserves are re-measured for the new frame. */
  let hallReservesPublished = false;
  /** Retry budget for publishHallReserves — see the guard at its end. */
  let hallReserveAttempts = 0;
  const HALL_RESERVE_MAX_ATTEMPTS = 600;

  /**
   * Lays out every hall's sculpture reserve — the band the marble is going to
   * occupy — and publishes it on that hall as `--reserve-{t,b}` plus
   * `.is-sculpture-reserved`.
   *
   * The reserve is measured from the animation's *tracks*, sampled across each
   * hall's whole segment (SpiritStage#reservedSubjectRect), so it is a pure
   * function of the viewport rather than of where the animation currently is.
   * That is the point: a hall can lay itself out around a statue that has not
   * arrived yet. Driving the layout off a live "is it standing right now"
   * measurement is what the halls must not do — it leaves the artwork in the
   * reference composition for the entire arrival and only moves it afterwards,
   * i.e. after it has already been covered.
   *
   * Portrait only. Landscape frames the sculpture high and beside the artwork —
   * the reference composition — so there is nothing to correct there, and the
   * reserve is simply removed. Same for static mode, which has no WebGL statue.
   *
   * Retried (see applyScrollVisuals) until it can actually measure: the stage
   * object exists before its models have finished loading, and a reserve of null
   * would silently leave the first hall unprotected.
   */
  function publishHallReserves() {
    const segments = [RUNWAY_SEG.hall1, RUNWAY_SEG.hall2, RUNWAY_SEG.hall3];
    const measurable = Boolean(stage) && Boolean(runway)
      && window.innerWidth <= window.innerHeight;

    let measured = false;
    for (let i = 0; i < segments.length; i++) {
      const hall = runway?.halls?.[i];
      const el = hall ? runway.hallEls?.[hall.id] : null;
      if (!el) continue;

      // Each hall reserves for its OWN sculpture — `halls[i].spirit` is the
      // model key — so a change in one spirit's track can never move another
      // hall's layout. The three are separate exhibitions sharing a corridor,
      // not one composition.
      const box = measurable && hall.spirit
        ? stage.reservedSubjectRect(hall.spirit, r2t(segments[i][0]), r2t(segments[i][1]))
        : null;
      el.classList.toggle('is-sculpture-reserved', Boolean(box));
      if (!box) continue;

      measured = true;
      // The band the artwork may use runs from under the bar to the marble's
      // crown, and the heading lives in the strip below it — both expressed the
      // way the hall's own children position themselves (insets from its edges).
      //
      // `box` is in the canvas's own client-pixel space (the WebGL layer is now
      // 100dvh, i.e. the *visible* viewport, not the larger 100vh layout
      // viewport), so the footprint must be measured against that same visible
      // height — `window.innerHeight` is the layout viewport and would leave the
      // strip sitting behind the browser's bottom toolbar.
      const visH = stage.container?.clientHeight ?? window.innerHeight;
      el.style.setProperty('--reserve-t', `${Math.round(box.y)}px`);
      el.style.setProperty(
        '--reserve-b',
        `${Math.round(visH - box.y - box.h)}px`
      );
    }

    // Nothing left to wait for once something has been measured — or once it is
    // clear that this viewport will never have a reserve. The attempt budget is
    // the third case: a stage that booted but whose models never arrived would
    // otherwise have this run a few hundred projections on every scroll frame,
    // for the rest of the session, to keep re-deriving null.
    hallReservesPublished = measured || !measurable
      || ++hallReserveAttempts >= HALL_RESERVE_MAX_ATTEMPTS;
  }

  /**
   * Applies scroll-driven visuals from the current (Lenis-smoothed) scrollY.
   * Called on init, resize, and every Lenis 'scroll' event.
   */
  function applyScrollVisuals() {
    const scrollY = window.scrollY;
    const heroH = window.innerHeight;

    // Hero text overlay rides up with scroll.
    if (textOverlay) {
      textOverlay.style.transform = `translateY(${-Math.min(scrollY, heroH)}px)`;
    }

    // Glass cracks follow scroll (internal handler manages reverse + fade).
    if (cracksCanvasEl) {
      cracksCanvasEl.style.transform = `translateY(${-Math.min(scrollY, heroH)}px)`;
    }

    // Bidirectional text exit: below ~15 % scroll → exit, above → return.
    if (heroContent?.classList.contains('is-revealed')) {
      const shouldExit = scrollY > heroH * 0.15;
      if (shouldExit && !heroExitActive) {
        heroExitActive = true;
        heroContent.classList.add('is-exiting');
      } else if (!shouldExit && heroExitActive) {
        heroExitActive = false;
        heroContent.classList.remove('is-exiting');
      }
    }

    // Story progress + runway + rail — driven by live Lenis-smoothed values.
    if (runway) {
      const targetR = runway.targetProgress;
      // Unified story-progress calculation:
      // - Hero zone (targetR <= 0): t from eased hero scroll (0 → 0.16)
      // - Runway zone (targetR > 0): t from eased runway progress (0.16 → 0.93)
      let t;
      if (targetR <= 0) {
        const heroT = Math.max(0, Math.min(1, scrollY / window.innerHeight));
        t = heroT * HERO_T_END;
      } else {
        t = HERO_T_END + targetR * RUNWAY_T_SPAN;
      }
      stage?.setStoryProgress(t);
      runway.update(targetR);
      updateRail(t, targetR <= 0 ? null : targetR);

      // Hero layering. While the hero is the current chapter the sculpture and
      // the title are lifted above the nav bar, so David reads as flying in
      // front of it instead of being sliced by it — his top projects above the
      // viewport in this framing, i.e. straight into the bar's band. Dropped as
      // soon as the runway begins, by which point the track has already walked
      // him down to his seat, clear of the bar. See `.is-hero` in main.css.
      document.body.classList.toggle('is-hero', targetR <= 0);
    }

    // Hall reserves are measured from the tracks, not from the clock, but the
    // tracks need the models — so this retries until they exist and then stops.
    if (!hallReservesPublished) publishHallReserves();
  }

  // ── Smoothing ─────────────────────────────────────────────────────────────
  // Plain Lenis easing: one continuous glide, nothing else touching the wheel.
  //
  // There used to be a "speed ceiling" here that derived a maximum lead from
  // `peak velocity ≈ |lead| × max-slope(easing) / duration` and, from inside
  // the ticker, re-aimed Lenis's running ease whenever the lead grew past it.
  // It was a latency hit and it fought the user: every wheel event that widened
  // the lead re-aimed the ease, so a fast or free-spinning wheel never got to
  // spend its travel — the page read as sticking. Removed. `duration` below is
  // the only smoothing knob now.
  const SCROLL_DURATION = 1.5;
  /** Cubic-out: gentle start, long settle. */
  const SCROLL_EASE = (t) => 1 - Math.pow(1 - t, 3);
  /** Quadratic-out for jumps: gentler still so a long pan settles smoothly. */
  const JUMP_EASE = (t) => 1 - Math.pow(1 - t, 2);
  /** Pacing reference for navigation jumps, in px/s (travel ÷ speed → duration). */
  const MAX_SCROLL_SPEED = 2800;

  if (!isStatic) {
    // Lenis: time-based smoothing, tuned heavy.
    //
    // The old curve was expo-out (`1 - 2^-10t`) at 1.15s. Expo-out starts at
    // full speed — the very first frames after a flick carry the largest
    // scroll delta, i.e. the worst frame cost lands exactly when the browser
    // is least able to absorb it. On a scene holding two 375k-triangle
    // sculptures that reads as a stutter.
    //
    // Heavy tuning instead: 1.5s settle, cubic-out (initial slope 3 vs ~6.9),
    // and a 0.85 wheel multiplier so one notch travels less. These are plain
    // Lenis options and the only smoothing knobs — nothing else touches the
    // wheel, so a gesture always gets to spend the travel it was given.
    lenis = new Lenis({
      duration: SCROLL_DURATION,
      easing: SCROLL_EASE,
      smoothWheel: true,
      wheelMultiplier: 0.85,
      touchMultiplier: 1.2,
    });

    // Official GSAP + Lenis wiring (order matters: ScrollTrigger.update runs
    // synchronously, so runway.targetProgress is fresh for our handler).
    lenis.on('scroll', ScrollTrigger.update);
    lenis.on('scroll', applyScrollVisuals);

    tickerCallback = (time) => {
      lenis?.raf(time * 1000);
    };
    gsap.ticker.add(tickerCallback);
    gsap.ticker.lagSmoothing(0);

    // Initial paint + resize (heroH changes).
    applyScrollVisuals();
    window.addEventListener('resize', applyScrollVisuals, { passive: true });
    // A hall's reserve is viewport-derived, so any reframe invalidates it.
    const refitHallReserves = () => {
      hallReservesPublished = false;
      publishHallReserves();
    };
    window.addEventListener('resize', refitHallReserves, { passive: true });
    window.addEventListener('orientationchange', refitHallReserves, { passive: true });

    // Browser chrome (address bar / bottom toolbar) resizes the *visible* area
    // without always firing a layout-affecting window resize, so the
    // visualViewport events are the reliable signal that the occluded band
    // changed. Re-derive the insets and re-measure the reserves so the artwork
    // clears the new visible bottom instead of the now-hidden layout edge.
    if (window.visualViewport) {
      const onChromeResize = () => {
        applyVisualViewport();
        hallReservesPublished = false;
        publishHallReserves();
      };
      window.visualViewport.addEventListener('resize', onChromeResize, { passive: true });
      window.visualViewport.addEventListener('scroll', onChromeResize, { passive: true });
    }
  }

  // Hero side cards: chapter index into the runway. Delegated on the scroller
  // so the marquee's dynamically-cloned cards respond too. Static mode keeps
  // native anchor behavior (smooth-scroll via CSS).
  const heroScrollerEl = document.getElementById('hero-scroller');
  /** @type {(e: Event) => void} */
  const onHeroCardClick = (event) => {
    if (isStatic) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const card = /** @type {HTMLElement|null} */ (target.closest('.hero-card'));
    if (!card) return;
    const spirit = card.dataset.spirit;
    if (!spirit) return;
    event.preventDefault();
    jumpToHall(spirit);
  };
  heroScrollerEl?.addEventListener('click', onHeroCardClick);

  // Nav anchor links (#hero, #runway, #about, #contact) — intercept and smooth.
  /** @type {Array<{ link: Element, handler: (e: Event) => void }>} */
  const navLinkHandlers = [];
  if (!isStatic) {
    document.querySelectorAll('#nav a[href^="#"]').forEach((link) => {
      const handler = /** @type {(e: Event) => void} */ ((event) => {
        const hash = link.getAttribute('href');
        if (!hash || hash === '#') return;
        const target = document.querySelector(hash);
        if (target) {
          event.preventDefault();
          // Still hand Lenis the element (it knows about offsets and the pinned
          // runway). The duration only needs an approximate distance, so a
          // bounding-rect read is enough — and it stops a far nav target from
          // being crossed at a much higher velocity than a near one.
          const distance = Math.abs(target.getBoundingClientRect().top);
          lenis?.scrollTo(target, {
            duration: clampJumpDuration(distance),
            easing: JUMP_EASE,
          });
        }
      });
      link.addEventListener('click', handler);
      navLinkHandlers.push({ link, handler });
    });
  }

  const lightbox = new Lightbox(lightboxContainer, {
    onOpen: () => lenis?.stop(),
    onClose: () => lenis?.start(),
  });
  new Footer(footerContainer);

  // ── Custom cursor (desktop only, auto-disabled on touch) ────────────
  const cursor = new CustomCursor();

  // ── Background music: one looping track per hall, over a base bed ───
  // The walk is a chain of stations — base, hall I, hall II, hall III, base —
  // and the handover between them is driven by the *scroll position* rather
  // than by a timer: the runway's progress r is already smooth (Lenis owns it)
  // and already knows where the corridors are, so a boundary in r is exactly a
  // corridor in the exhibition. The base track covers everything that is not a
  // hall: the hero before the visitor walks in, and the outro after the last
  // room. The fade curves are equal-power (cos/sin, chained across every
  // boundary) because two linear fades audibly dip in the middle of a handover.
  //
  // Autoplay keeps the bargain it had before: try with sound, and if the
  // browser refuses (cold visit), keep the stream warm and silent and unmute
  // on the first real user activation.
  function setupBgm() {
    const btn = document.getElementById('bgm-toggle');
    if (!btn) return;

    const BASE = import.meta.env.BASE_URL;
    /** One file per hall; any hall past the third reuses the last track. */
    const HALL_SRC = ['audio/hall1.mp3', 'audio/hall2.mp3', 'audio/hall3.mp3'];
    const count = Math.max(1, halls.length);

    const mkAudio = (src) => {
      const el = document.createElement('audio');
      el.src = BASE + src;
      el.loop = true;
      el.preload = 'auto';
      // Silent until a real gesture grants sound. Muted playback is always
      // allowed, so the current track is streaming before the visitor touches
      // anything — which is what makes the first gesture instant.
      el.muted = true;
      el.setAttribute('aria-hidden', 'true');
      return el;
    };

    // ── The walk, as a chain of stations ──────────────────────────────
    //   base ─ hall I ─ hall II ─ hall III ─ base
    // The base track owns both ends of that chain: the hero, before the
    // visitor walks in, and the outro after the last room. It is one element,
    // not two, so the file is fetched once and both ends stay in phase; its
    // gain is the sum of the two end weights of the chain.
    /** @type {{el: HTMLAudioElement, gain: number, title: string}[]} */
    const tracks = [{
      el: mkAudio('audio/bgm.mp3'),
      gain: 1,
      title: 'Cast in Exile',
    }];
    for (let i = 0; i < count; i += 1) {
      tracks.push({
        el: mkAudio(HALL_SRC[Math.min(i, HALL_SRC.length - 1)]),
        gain: 0,
        title: halls[i]?.title ?? '',
      });
    }

    const MASTER = 0.5;       // ceiling for the whole mix
    const CROSS_HALF = 0.055; // hall↔hall crossfade half-width, in runway r units
    const ENTER_R = 0.10;     // r by which the base track has given way to hall I
    const EXIT_R = 0.07;      // tail of the runway over which the base track returns
    const FOLLOW = 0.12;      // per-frame lerp toward the scroll-derived mix
    const FADE = 0.055;       // per-frame lerp toward the master target
    const AUDIBLE = 0.004;    // below this a track is out of the mix

    /** Whether to attempt music this session: '1' wants it, '0' opted out.
     *  Stored per-SESSION, deliberately not forever — a visitor who muted the
     *  music yesterday is still offered it today; "off" only needs to survive
     *  the current tab. (A permanent opt-out here also acted as a trap during
     *  testing: one toggle click silenced every future attempt AND the hint.) */
    const PREF_KEY = 'bgm-enabled';
    let pref = null;
    try {
      pref = sessionStorage.getItem(PREF_KEY);
      // Legacy value from the earlier localStorage version: a stuck '0' here
      // would mute every attempt for this browser forever. Drop it.
      localStorage.removeItem(PREF_KEY);
    } catch { /* private mode */ }
    const rememberPref = (on) => {
      try { sessionStorage.setItem(PREF_KEY, on ? '1' : '0'); } catch { /* ignore */ }
    };

    let wantOn = pref !== '0'; // the visitor's intent
    let muted = true;          // the browser has not granted audible playback
    let masterGain = 0;
    let progress = null;       // latest runway progress, r ∈ [0, 1]
    let lastPressed = null;
    let lastMetaIdx = -1;
    let lastState = '';

    const setPressed = (on) => {
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    };

    // The icon must tell the truth: "on" only when music is actually audible
    // (wanted, unmuted, and something is really playing). Derived from the
    // engine's own state rather than from any call site, so every path that
    // starts or stops playback keeps it honest for free.
    const isAudible = () =>
      wantOn && !muted && tracks.some((t) => !t.el.paused);
    const syncBtn = () => {
      const on = isAudible();
      if (on === lastPressed) return;
      lastPressed = on;
      setPressed(on);
    };

    /**
     * Per-track gains for the current scroll position.
     *
     * Every boundary of the station chain gets a mix parameter that runs
     * 0 → 1 across its window, and the gains are the equal-power pair cos/sin
     * of that parameter — chained, so the total power stays flat even though
     * there are four boundaries and three halls.
     *
     * The two end boundaries are the base track's: `enter` hands the hero over
     * to hall I as the visitor walks in, `exit` brings the base back over the
     * runway's tail. Both write to the same element (index 0), and the two
     * windows are far apart, so it is never counted twice.
     * @returns {number[]}
     */
    const targetGains = () => {
      const n = tracks.length; // base + one per hall
      const out = new Array(n).fill(0);
      if (n === 1) { out[0] = 1; return out; }

      const quarter = Math.PI / 2;
      // `null` means the visitor is still in the hero, before the runway.
      const r = progress;
      const ramp = (from, to) =>
        Math.min(1, Math.max(0, ((r ?? 0) - from) / (to - from)));

      const enter = r === null ? 0 : ramp(0, ENTER_R);
      const exit = r === null ? 0 : ramp(1 - EXIT_R, 1);
      const hallMixes = HALL_BOUNDARIES
        .slice(0, n - 2)
        .map((b) => ramp(b - CROSS_HALF, b + CROSS_HALF));

      // n stations' worth of boundaries: base→I, I→II, …, last→base.
      const borders = [enter, ...hallMixes, exit];
      out[0] = Math.cos(borders[0] * quarter)
        + Math.sin(borders[borders.length - 1] * quarter);
      for (let i = 1; i < n; i += 1) {
        out[i] = Math.sin(borders[i - 1] * quarter)
          * Math.cos(borders[i] * quarter);
      }
      return out;
    };

    // ── OS media-session metadata ──────────────────────────────────────
    // Without MediaMetadata the OS media surface (lock screen, notification
    // shade, media-key popup) shows a bare "this tab is playing audio" entry.
    // The title follows whichever hall currently owns the mix, so the lock
    // screen names the room the visitor is standing in.
    const artwork = [{
      src: `${BASE}david-artwork.png`,
      sizes: '470x470',
      type: 'image/png',
    }];
    const publishMeta = (idx) => {
      if (!('mediaSession' in navigator)) return;
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: tracks[idx].title || 'Exhibition',
          artist: 'The Exile of Plaster Statues',
          album: 'Exhibition BGM',
          artwork,
        });
      } catch { /* metadata is decoration; never fatal */ }
    };
    if ('mediaSession' in navigator) {
      try {
        navigator.mediaSession.setActionHandler('play', () => {
          wantOn = true;
          muted = false;
          rememberPref(true);
          unlockWebAudio();
          playLive();
        });
        navigator.mediaSession.setActionHandler('pause', () => {
          wantOn = false;
          rememberPref(false);
        });
      } catch {
        // A browser that has mediaSession but rejects a handler type must not
        // take the whole music setup down.
      }
    }

    // ── Web Audio unlock (the industry-standard fix) ──────────────────
    // Chrome's own guidance: create the AudioContext up front (it starts
    // suspended), then on the first user gesture resume() it and start one
    // silent frame — the browser then treats the origin as audio-enabled.
    /** @type {AudioContext|null} */
    let audioCtx = null;
    const unlockWebAudio = () => {
      try {
        const Ctx = window.AudioContext
          || /** @type {any} */ (window).webkitAudioContext;
        if (!Ctx) return;
        if (!audioCtx) audioCtx = new Ctx();
        if (audioCtx.state === 'suspended') {
          audioCtx.resume().catch(() => {});
        }
        const frame = audioCtx.createBuffer(1, 1, 22050);
        const source = audioCtx.createBufferSource();
        source.buffer = frame;
        source.connect(audioCtx.destination);
        source.start();
      } catch { /* best-effort unlock */ }
    };

    /**
     * First gesture anywhere: make the music audible.
     *
     * Two event families, handled differently on purpose. The activation
     * events (`pointerup` / `touchend` / `click` / `keydown` — notably
     * `touchend` for iOS Safari, where `pointerdown` does NOT count) are the
     * set browsers actually honour as user activation; the first of them
     * unlocks Web Audio, unmutes and starts playback, then retires every
     * listener. `wheel` / `scroll` / `mousemove` are different: desktop
     * Chrome refuses to treat any of them as activation, so their play() is
     * rejected no matter what — but a flick of the wheel or a moving pointer
     * is usually the first thing a desktop visitor does, so they keep
     * ATTEMPTING (throttled) and never retire the activation listeners, ready
     * for the first genuine click.
     */
    const GESTURE_EVENTS = ['pointerup', 'touchend', 'click', 'keydown'];
    const WEAK_EVENTS = ['wheel', 'scroll', 'mousemove'];
    let lastWeakAttempt = 0;

    /** Halls currently in the mix — or the first hall as a stand-in. */
    const liveHalls = () => {
      const live = tracks.filter((t) => t.gain > AUDIBLE);
      return live.length ? live : [tracks[0]];
    };

    const playLive = () => {
      tracks.forEach((t) => { t.el.muted = false; });
      liveHalls().forEach((t) => t.el.play().catch(() => {}));
    };

    const attemptUnlock = (strong) => {
      unlockWebAudio();
      if (!wantOn) { if (strong) detachGestures(); return; }
      // Already audible — nothing to add.
      if (!muted && isAudible()) { if (strong) detachGestures(); return; }
      muted = false;
      playLive();
      if (strong) detachGestures();
    };

    const onActivation = (event) => {
      // Tapping the toggle itself must stay a pure toggle: its own click
      // handler starts playback, and an unlock here would race it into
      // "started then immediately paused".
      if (event.target instanceof Element && event.target.closest('#bgm-toggle')) {
        return;
      }
      attemptUnlock(true);
    };

    const onWeakGesture = () => {
      const now = performance.now();
      if (now - lastWeakAttempt < 1200) return;
      lastWeakAttempt = now;
      attemptUnlock(false);
    };

    const detachGestures = () => {
      GESTURE_EVENTS.forEach((type) =>
        document.removeEventListener(type, onActivation)
      );
      WEAK_EVENTS.forEach((type) =>
        document.removeEventListener(type, onWeakGesture)
      );
    };

    GESTURE_EVENTS.forEach((type) =>
      document.addEventListener(type, onActivation, { passive: true })
    );
    WEAK_EVENTS.forEach((type) =>
      document.addEventListener(type, onWeakGesture, { passive: true })
    );

    /**
     * Begin playback. Succeeds outright on desktop Chrome with a high Media
     * Engagement Index (the browser autoplays sites the user usually lets
     * play); when it rejects, the silent stream below keeps the current hall
     * warm so the first gesture produces instant sound.
     */
    const start = () => {
      if (!wantOn) { syncBtn(); return; }
      const first = tracks[0].el;
      const p = first.play(); // muted → always allowed
      if (!p || !p.catch) { syncBtn(); return; }
      p.catch(() => { /* nothing to do: it stays silent until a gesture */ });
      // Try to go straight to sound as well. This is the path engaged/returning
      // visitors take; if the browser refuses, re-mute and wait for a gesture.
      first.muted = false;
      const q = first.play();
      if (q && q.catch) {
        q.catch(() => { first.muted = true; });
      }
    };

    // Toggle button: manual on/off. The choice is remembered for the session.
    btn.addEventListener('click', () => {
      if (wantOn) {
        wantOn = false;
        rememberPref(false);
        tracks.forEach((t) => t.el.pause());
        lastPressed = null;
        syncBtn();
      } else {
        wantOn = true;
        muted = false;
        rememberPref(true);
        unlockWebAudio();
        playLive();
      }
    });

    /**
     * The mix runs every frame, but it is not an animation — it follows the
     * scroll. `targetGains()` reads the live runway progress, and the small
     * lerp on top only exists to absorb discontinuities (a reload landing
     * mid-page, a rail jump), never to add a second smoothing stage.
     */
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const target = targetGains();
      const head = wantOn ? MASTER : 0;
      masterGain += (head - masterGain) * FADE;

      let metaIdx = 0;
      for (let i = 0; i < tracks.length; i += 1) {
        const track = tracks[i];
        track.gain += (target[i] - track.gain) * FOLLOW;
        if (track.gain < 0.003) track.gain = 0;
        track.el.volume = Math.min(1, track.gain * masterGain);
        if (track.gain > tracks[metaIdx].gain) metaIdx = i;

        // Only the halls actually in the mix keep a stream open, so scrolling
        // into a corridor costs two decoders, not three.
        if (wantOn && track.gain > AUDIBLE) {
          if (track.el.paused) track.el.play().catch(() => {});
        } else if (!track.el.paused) {
          track.el.pause();
        }
      }

      if (wantOn && metaIdx !== lastMetaIdx) {
        lastMetaIdx = metaIdx;
        publishMeta(metaIdx);
      }
      if ('mediaSession' in navigator) {
        const state = isAudible() ? 'playing' : 'paused';
        if (state !== lastState) {
          lastState = state;
          navigator.mediaSession.playbackState = state;
        }
      }
      syncBtn();
    };
    raf = requestAnimationFrame(tick);

    bgmSetProgress = (r) => { progress = r; };
    bgmStop = () => {
      cancelAnimationFrame(raf);
      tracks.forEach((t) => { t.el.pause(); t.el.src = ''; });
      detachGestures();
      bgmSetProgress = null;
      bgmStop = null;
    };

    // Autoplay as the hero reveals (desktop + static alike).
    if (tracker) {
      tracker.ready.then(start);
    } else {
      start();
    }
  }
  setupBgm();

  /**
   * Centralised cleanup: called on beforeunload AND pagehide (the latter is
   * more reliable on mobile iOS where beforeunload often doesn't fire).
   */
  function cleanup() {
    cursor?.destroy();
    lightbox.destroy();
    stage?.destroy();
    runway?.destroy();
    bgmStop?.();
    ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    crackCleanup?.destroy();
    crackCleanup = null;

    // Lenis cleanup
    if (lenis) {
      lenis.destroy();
      lenis = null;
    }
    gsap.ticker.remove(tickerCallback);

    heroScrollerEl?.removeEventListener('click', onHeroCardClick);
    heroMarquee?.destroy();
    navLinkHandlers.forEach(({ link, handler }) =>
      link.removeEventListener('click', handler)
    );
    window.removeEventListener('resize', applyScrollVisuals);
  }

  window.addEventListener('beforeunload', cleanup);
  window.addEventListener('pagehide', cleanup);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
